import { useEffect, useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "../site/Reveal";
import { getPublicConfig } from "@/lib/track";

const DELIVERABLES = [
  "Complete GoHighLevel B2B Systems Architecture (Custom Pipelines, Triggers & Mailhooks)",
  "Full Guest/Customer Journey & Revenue Leak Audit",
  "Done-for-You Database Reactivation Pulse (Monetize past contact lists within 14 days)",
  "Private Dispute Resolution & Review Recovery Engine",
  "VIP Onboarding + Monthly 1-on-1 Strategic Advisory Calls",
];

export default function Transformation() {
  const [bookingUrl, setBookingUrl] = useState("");

  useEffect(() => {
    getPublicConfig().then((c) => setBookingUrl(c.scheduling_link || ""));
  }, []);

  return (
    <section
      id="transformation"
      data-testid="transformation-section"
      className="relative bg-[hsl(35_22%_95%)] text-foreground py-24 lg:py-32 grain overflow-hidden"
    >
      <div className="container-luxe relative z-10">
        <Reveal>
          <p className="overline flex items-center gap-3">
            <span className="h-px w-8 bg-[var(--gold)]" />
            The Flagship Engagement
          </p>
          <h2 className="mt-6 max-w-4xl serif text-[clamp(2rem,4.6vw,3.8rem)] leading-[1.04] tracking-tight">
            The Sovereign CEO{" "}
            <span className="italic text-gold">90-Day Reset</span> &amp; Systems
            Transformation.
          </h2>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-muted-foreground">
            A 90-Day Done-For-You Advisory Partnership to audit, build, and scale
            your operational backbone.
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[var(--gold)]">
            For Hospitality &amp; Accommodations · Service-Based Businesses · Experience &amp; Community Brands
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-14 items-start">
          {/* Deliverables */}
          <Reveal delay={0.1}>
            <div className="border border-border bg-background p-9 lg:p-11">
              <p className="overline">Key Deliverables</p>
              <ul className="mt-7 space-y-5" data-testid="transformation-deliverables">
                {DELIVERABLES.map((d, i) => (
                  <li key={d} className="flex items-start gap-4" data-testid={`deliverable-${i}`}>
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center border border-[var(--gold)]">
                      <Check size={13} className="text-[var(--gold)]" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm lg:text-base leading-relaxed text-foreground/85">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {/* Pricing callout */}
          <Reveal delay={0.2}>
            <div
              data-testid="transformation-pricing"
              className="border border-[var(--gold)] bg-[var(--charcoal)] text-[var(--cream)] p-9 lg:p-11"
            >
              <p className="overline !text-[var(--gold-soft)]">Your Investment</p>
              <div className="mt-6 space-y-6">
                <div>
                  <p className="serif text-[clamp(2rem,3.6vw,2.9rem)] leading-none tracking-tight gold-shimmer" data-testid="transformation-price-full">
                    $10,000
                  </p>
                  <p className="mt-2 text-sm text-[var(--cream)]/65">Paid in full</p>
                </div>
                <div className="border-t border-white/12 pt-6">
                  <p className="serif text-2xl tracking-tight" data-testid="transformation-price-installments">
                    3 × $3,600
                  </p>
                  <p className="mt-2 text-sm text-[var(--cream)]/65">Monthly installments</p>
                </div>
              </div>
              <a
                href={bookingUrl || "#audit"}
                target={bookingUrl ? "_blank" : undefined}
                rel={bookingUrl ? "noopener noreferrer" : undefined}
                data-testid="transformation-cta"
                className="group mt-9 inline-flex w-full items-center justify-center gap-3 bg-[var(--gold)] px-8 py-4 text-sm text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors duration-400"
              >
                Book Advisory Call
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <p className="mt-4 text-xs leading-relaxed text-[var(--cream)]/45">
                Scoped to your operation on your Advisory Call. Begins with the
                FREE Time-Wealth Audit above.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
